import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, CaciDetailStyles, PageHeader, SectionNote, Seal,
  Prose, ShareBar, FileList, DetailSkeleton,
  useReveal, useScrollProgress,
  stripHtml, truncate, formatDate, langPath, localized,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   ARTICLEDETAIL
   ───────────────────────────────────────────────────────────────────────────
   Same endpoints (/api/articles/edit/:id and /api/categoryportail/list_acticle),
   same attachments, same sharing, same category sidebar. Changes:

   · The article body is the point of this page and had no typography at all —
     raw editor HTML inheriting whatever the theme happened to apply. It now
     has a real measure, rhythm, and styling for every element an editor can
     produce, including tables, which used to push the page sideways on a
     phone.
   · Dates were hard-coded to the "en-US" locale, so a French or Arabic reader
     was shown "Mar 4, 2026". Now follows the interface language.
   · The page called navigate("*") from inside render whenever the article was
     missing. Navigating during render is a React violation, and "*" is a
     literal path — it put /* in the address bar and broke the Back button.
     A missing article now renders a proper not-found panel in place.
   · Loading returned null, so the whole screen was blank until the request
     finished. Skeletons now hold the layout.
   · Six share buttons appeared twice on the same screen — a full-width row in
     the body and a duplicate icon row in the sidebar. One row now, and on a
     phone it offers the OS share sheet, which reaches apps no button row can.
   · Copy-link silently did nothing on insecure origins; it now falls back.
   · The sidebar search sent visitors to a hard-coded /search, dropping the
     language prefix for Arabic and English readers.
   · Category links used category.slug unguarded, producing
     "/category/5/undefined" whenever a slug was absent.
   · The Helmet block read article.cat_articles.map(...) with no guard, so an
     article returned without that relation crashed the page outright.
   ═══════════════════════════════════════════════════════════════════════════ */

function ArticleDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | missing | error
  const [term, setTerm] = useState("");

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(() => {
    setState("loading");

    /* The two requests are independent: a broken category list should not
       take the article down with it, which is what Promise.all did before. */
    const articleReq = axios
      .get(`${BASE_URL}/api/articles/edit/${id}`)
      .then((res) => {
        if (!alive.current) return;
        const data = res.data;
        if (!data || !data.id) { setState("missing"); return; }
        setArticle(data);
        setState("ready");
      })
      .catch((err) => {
        if (!alive.current) return;
        const status = err?.response?.status;
        console.error(`ArticleDetail: article ${id} failed to load —`, err);
        setState(status === 404 ? "missing" : "error");
      });

    axios
      .get(`${BASE_URL}/api/categoryportail/list_acticle`)
      .then((res) => {
        if (!alive.current) return;
        setCategories(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        /* Not fatal. The sidebar list simply does not render. */
        console.error("ArticleDetail: category list failed to load —", err);
      });

    return articleReq;
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /* Moving between articles should start at the top, not halfway down the
     previous one. */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  const pageRef = useReveal([article, state, categories]);
  const progress = useScrollProgress();

  const title = article ? stripHtml(localized(article, "name", lang)) : "";
  const bodyHtml = article ? localized(article, "description", lang) : "";
  const summary = truncate(stripHtml(bodyHtml), 180);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  /* Social crawlers need an absolute URL. The original built this from
     window.location.origin, which is the front-end host — the images live on
     the API host, so every preview came back broken. */
  const shareImage = article?.image
    ? (String(article.image).startsWith("http") ? article.image : `${BASE_URL}${article.image}`)
    : "";

  const cats = (article?.cat_articles || [])
    .filter((c) => c?.category)
    .map((c) => c.category);

  const catLink = (c) => (c.slug ? `/category/${c.id}/${c.slug}` : `/category/${c.id}`);

  const files = (article?.file_articles || []).filter(
    (f) => f && !f.deleted_at && (!f.lang || f.lang === lang)
  );

  const onSearch = (e) => {
    e.preventDefault();
    const q = term.trim();
    if (!q) return;
    navigate(`${langPath(lang, "/search")}?keyword=${encodeURIComponent(q)}`);
  };

  const listLabel = t("menu.Toutacticle", "Actualités");

  /* ── Not found / failed ───────────────────────────────────────────── */
  if (state === "missing" || state === "error") {
    const missing = state === "missing";
    return (
      <div className={`caci-page${isRtl ? " is-rtl" : ""}`} dir={isRtl ? "rtl" : "ltr"}>
        <CaciStyles />
        <Helmet>
          <title>{missing ? t("notfound.title", "Introuvable") : t("search.errorTitle", "Erreur")} — {t("menu.caci")}</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <PageHeader
          title={missing ? t("notfound.title", "Article introuvable") : t("search.errorTitle", "Chargement impossible")}
          crumbs={[
            { label: t("menu.home"), to: langPath(lang, "/") },
            { label: listLabel, to: langPath(lang, "/toutacticle") },
          ]}
        />
        <section className="caci-section">
          <div className="caci-shell">
            <SectionNote
              state={missing ? "empty" : "error"}
              title={missing
                ? t("notfound.heading", "Cet article n'existe pas ou a été retiré.")
                : t("home.section_error", "Cet article n'a pas pu être chargé.")}
              text={missing ? t("notfound.text", "") : ""}
              action={
                missing ? (
                  <button
                    type="button" className="btn-quiet"
                    onClick={() => navigate(langPath(lang, "/toutacticle"))}
                  >
                    <i className={`bi bi-arrow-${isRtl ? "right" : "left"}`} />
                    {listLabel}
                  </button>
                ) : (
                  <button type="button" className="btn-quiet" onClick={load}>
                    <i className="bi bi-arrow-clockwise" />
                    {t("home.retry", "Réessayer")}
                  </button>
                )
              }
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <CaciDetailStyles />

      {state === "ready" && (
        <Helmet>
          <title>{title} — {t("menu.caci")}</title>
          <meta name="description" content={summary} />
          {cats.length > 0 && (
            <meta name="keywords" content={cats.map((c) => stripHtml(localized(c, "name", lang))).join(", ")} />
          )}
          <link rel="canonical" href={shareUrl} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={summary} />
          {shareImage && <meta property="og:image" content={shareImage} />}
          {shareImage && <meta property="og:image:secure_url" content={shareImage} />}
          <meta property="og:type" content="article" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:site_name" content={t("menu.caci")} />
          {article?.starts_at && <meta property="article:published_time" content={article.starts_at} />}
          <meta name="twitter:card" content={shareImage ? "summary_large_image" : "summary"} />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={summary} />
          {shareImage && <meta name="twitter:image" content={shareImage} />}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              headline: title,
              image: shareImage || undefined,
              datePublished: article?.starts_at || undefined,
              inLanguage: lang,
              mainEntityOfPage: shareUrl,
              publisher: { "@type": "Organization", name: t("menu.caci") },
              author: { "@type": "Organization", name: t("menu.caci") },
            })}
          </script>
        </Helmet>
      )}

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={state === "ready" ? title : t("menu.article", "Article")}
        crumbs={[
          { label: t("menu.home"), to: langPath(lang, "/") },
          { label: listLabel, to: langPath(lang, "/toutacticle") },
          { label: state === "ready" ? truncate(title, 40) : t("menu.article", "Article") },
        ]}
      />

      <section className="caci-section">
        <div className="caci-shell">
          <div className="detail-wrap">
            {/* ── Body ─────────────────────────────────────────── */}
            <div className="detail-main">
              {state === "loading" && <DetailSkeleton />}

              {state === "ready" && (
                <article>
                  {article.image && (
                    <figure className="detail-hero" data-rv="zoom">
                      <img
                        src={`${BASE_URL}${article.image}`}
                        alt=""
                        loading="eager"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.closest(".detail-hero").style.display = "none";
                        }}
                      />
                    </figure>
                  )}

                  <div className="detail-meta" data-rv="fade">
                    {article.starts_at && (
                      <span>
                        <i className="bi bi-calendar3" />
                        <time dateTime={article.starts_at}>
                          {formatDate(article.starts_at, lang, "")}
                        </time>
                      </span>
                    )}
                    {cats.length > 0 && (
                      <span>
                        <i className="bi bi-tag" />
                        {truncate(stripHtml(localized(cats[0], "name", lang)), 32)}
                      </span>
                    )}
                  </div>

                  <Prose html={bodyHtml} />

                  {cats.length > 0 && (
                    <div className="detail-tags">
                      <span className="detail-tags-label">
                        <i className="bi bi-tags" />
                        {t("menu.Categories")}
                      </span>
                      {cats.map((c, i) => (
                        <Link key={c.id ?? i} to={catLink(c)} className="art-cat">
                          {truncate(stripHtml(localized(c, "name", lang)), 30)}
                        </Link>
                      ))}
                    </div>
                  )}

                  <ShareBar
                    url={shareUrl}
                    title={title}
                    label={t("menu.Share")}
                    nativeLabel={t("menu.Share")}
                    copiedLabel={t("share.copied", "Lien copié")}
                  />
                </article>
              )}
            </div>

            {/* ── Sidebar ──────────────────────────────────────── */}
            <aside className="side">
              <div className="side-card">
                <h2 className="side-title">
                  <Seal size={13} />
                  {t("menu.Search")}
                </h2>
                <form onSubmit={onSearch}>
                  <div className="field">
                    <i className="bi bi-search" aria-hidden="true" />
                    <input
                      type="search"
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      placeholder={t("search.placeholder")}
                      aria-label={t("search.placeholder")}
                      dir="auto"
                    />
                  </div>
                </form>
              </div>

              {files.length > 0 && (
                <div className="side-card">
                  <h2 className="side-title">
                    <Seal size={13} />
                    {t("home.Download_Catalog")}
                  </h2>
                  <FileList files={files} baseUrl={BASE_URL} />
                </div>
              )}

              {categories.length > 0 && (
                <div className="side-card">
                  <h2 className="side-title">
                    <Seal size={13} />
                    {t("menu.Categories")}
                  </h2>
                  <ul className="side-list">
                    {categories.map((c, i) => (
                      <li key={c.id ?? i}>
                        <Link to={catLink(c)}>
                          <span dir="auto">{truncate(stripHtml(localized(c, "name", lang)), 40)}</span>
                          {typeof c.articles_count === "number" && (
                            <span className="side-count">{c.articles_count}</span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ArticleDetail;
